import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Pencil, Receipt, Loader2, Download, Plus, Lock } from 'lucide-react'
import { api } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import toast from 'react-hot-toast'

const EMPTY_NOVO = {
  codigo: '', descricao: '', unidade: 'UN', ncm: '', cfop: '', cst: '10',
  csosn: '', cest: '', aliquota_icms: '', finalidade: 'revenda',
}

interface Prod {
  id: number; codigo: string | null; descricao: string | null
  ncm: string | null; cfop: string | null; cest: string | null
  cst: string | null; csosn: string | null
  aliquota_icms: number | null; mva_st: number | null; pauta_st: number | null
  aliquota_icms_st: number | null
  cst_pis: string | null; cst_cofins: string | null
  aliquota_pis: number | null; aliquota_cofins: number | null
  // FCP — adicional estadual. Aqui é o fallback intraestadual; por UF de
  // destino, a tabela de baixo manda.
  p_fcp: number | null; p_fcp_st: number | null
  // IBS/CBS — enquadramento da Reforma Tributária, definido pelo contador.
  cst_ibs_cbs: string | null; c_class_trib: string | null
  // Vínculo vivo: preenchido, os campos acima deixam de valer na emissão.
  regra_imposto_id: number | null
}

const N = (v: any) => (v === '' || v == null ? null : Number(v))

// Tributação por UF de destino (interestadual) — origem WinThor PCTABTRIB⋈PCTRIBUT (rotina 574)
function UfTributacao({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()
  const { data: ufs = [] } = useQuery<any[]>({
    queryKey: ['trib-uf', produtoId],
    queryFn: async () => (await api.get(`/fiscal/tributacao-produtos/${produtoId}/ufs`)).data,
  })
  const [rows, setRows] = useState<Record<string, any>>({})
  const [novoUf, setNovoUf] = useState({ uf: '', cst: '10', cfop: '', aliquota_icms: '', pauta_st: '', mva_st: '', cest: '', p_fcp: '', p_fcp_st: '' })
  // Pode haver 2 linhas por UF por FILIAL emitente (1=matriz, usada na emissão; 2=secundária).
  // Estado de edição chaveado por id da linha (não por UF) pra não misturar as duas.
  const get = (u: any, k: string) => (rows[u.id]?.[k] ?? u[k] ?? '')
  const filEmite = (id?: number) => (id == null || id === 1)
  const salvarUf = useMutation({
    mutationFn: async (payload: { uf: string; filial_id?: number; vals: any }) => (await api.put(
      `/fiscal/tributacao-produtos/${produtoId}/ufs/${payload.uf}`, {
        cst: payload.vals.cst || null, cfop: payload.vals.cfop || null, aliquota_icms: N(payload.vals.aliquota_icms),
        pauta_st: N(payload.vals.pauta_st), mva_st: N(payload.vals.mva_st), cest: payload.vals.cest || null,
        p_fcp: N(payload.vals.p_fcp), p_fcp_st: N(payload.vals.p_fcp_st),
      }, { params: { filial_id: payload.filial_id ?? 1 } })).data,
    onSuccess: () => {
      toast.success('UF salva'); setNovoUf({ uf: '', cst: '10', cfop: '', aliquota_icms: '', pauta_st: '', mva_st: '', cest: '', p_fcp: '', p_fcp_st: '' })
      qc.invalidateQueries({ queryKey: ['trib-uf', produtoId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  // FCP é definido por UF de destino — é aqui que ele realmente pertence.
  const cols = ['cst', 'cfop', 'aliquota_icms', 'pauta_st', 'mva_st', 'cest', 'p_fcp', 'p_fcp_st', 'cst_ibs_cbs', 'c_class_trib'] as const
  return (
    <div className="px-5 pb-4">
      <div className="text-xs font-semibold text-ink-600 uppercase mb-2">Tributação por UF de destino (interestadual)</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-ink-500 text-left">
            <th className="py-1">UF</th><th>Filial</th><th>CST</th><th>CFOP</th><th>ICMS %</th><th>Pauta ST</th><th>MVA %</th><th>CEST</th><th>FCP %</th><th>FCP ST %</th><th></th>
          </tr></thead>
          <tbody>
            {ufs.map(u => (
              <tr key={u.id} className="border-t border-ink-100">
                <td className="py-1 font-semibold">{u.uf}</td>
                <td>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${filEmite(u.filial_id) ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'}`}>
                    Filial {u.filial_id ?? 1}{filEmite(u.filial_id) ? ' • emite' : ''}
                  </span>
                </td>
                {cols.map(k => (
                  <td key={k} className="pr-1">
                    <input className="w-16 border border-ink-200 rounded px-1 py-0.5"
                      value={get(u, k)} onChange={e => setRows(r => ({ ...r, [u.id]: { ...r[u.id], [k]: e.target.value } }))} />
                  </td>
                ))}
                <td><button className="text-brand-600 hover:underline" onClick={() => salvarUf.mutate({ uf: u.uf, filial_id: u.filial_id ?? 1, vals: Object.fromEntries(cols.map(k => [k, get(u, k)])) })}>salvar</button></td>
              </tr>
            ))}
            {/* adicionar UF nova (filial emitente = 1) */}
            <tr className="border-t border-ink-200 bg-ink-50/50">
              <td className="py-1">
                <input className="w-12 border border-ink-300 rounded px-1 py-0.5 uppercase" placeholder="UF" maxLength={2}
                  value={novoUf.uf} onChange={e => setNovoUf({ ...novoUf, uf: e.target.value.toUpperCase() })} />
              </td>
              <td><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Filial 1</span></td>
              {cols.map(k => (
                <td key={k} className="pr-1">
                  <input className="w-16 border border-ink-300 rounded px-1 py-0.5"
                    value={(novoUf as any)[k]} onChange={e => setNovoUf({ ...novoUf, [k]: e.target.value })} />
                </td>
              ))}
              <td>
                <button className="text-emerald-700 font-medium hover:underline disabled:opacity-40"
                  disabled={novoUf.uf.length !== 2}
                  onClick={() => salvarUf.mutate({ uf: novoUf.uf, filial_id: 1, vals: novoUf })}>+ adicionar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {!ufs.length && <p className="text-[11px] text-ink-400 mt-1">Sem UF interestadual ainda — sem linha, a NF usa a tributação intraestadual acima. Adicione uma UF na linha de baixo.</p>}
    </div>
  )
}

export default function TributacaoProdutos() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [q, setQ] = useState('')
  const [comST, setComST] = useState(false)
  const [edit, setEdit] = useState<Prod | null>(null)
  const [novo, setNovo] = useState<any | null>(null)
  // Seleção p/ o lote. Set de ids — o filtro atual continua valendo em paralelo.
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [regraLote, setRegraLote] = useState<number | ''>('')

  // Só regras SEM filial podem ir pro produto (produto é da empresa, não de uma
  // loja). O backend recusa as outras; aqui elas nem aparecem.
  const { data: regras = [] } = useQuery<any[]>({
    queryKey: ['regras-imposto-produto'],
    queryFn: async () => {
      const r = (await api.get('/regras-imposto')).data.regras || []
      return r.filter((x: any) => !x.filial_id)
    },
  })
  const nomeRegra = (id?: number | null) =>
    id ? (regras.find((r: any) => r.id === id)?.nome || `Regra ${id}`) : null

  const aplicarLote = useMutation({
    mutationFn: async (simular: boolean) => (await api.post(
      '/fiscal/tributacao-produtos/regra-em-lote', {
        regra_id: regraLote === '' ? null : Number(regraLote),
        produto_ids: sel.size ? [...sel] : undefined,
        busca: sel.size ? undefined : (q || undefined),
        simular,
      })).data,
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aplicar'),
  })

  /** Simula, mostra os números, e só grava na confirmação — sobrescrever a regra
   *  de centenas de produtos sem aviso é o acidente que isto evita. */
  const confirmarLote = async () => {
    const s = await aplicarLote.mutateAsync(true)
    if (!s.afetados) { toast.error('Nenhum produto no filtro/seleção'); return }
    const alvo = regraLote === '' ? 'REMOVER a regra de' : `aplicar a regra em`
    const aviso = s.ja_com_outra_regra
      ? `\n\nATENÇÃO: ${s.ja_com_outra_regra} já têm outra regra e serão sobrescritos.`
      : ''
    if (!window.confirm(`Confirma ${alvo} ${s.afetados} produto(s)?${aviso}`)) return
    const r = await aplicarLote.mutateAsync(false)
    toast.success(`${r.aplicados} produto(s) atualizados`)
    setSel(new Set())
    qc.invalidateQueries({ queryKey: ['tributacao-produtos'] })
  }

  const criar = useMutation({
    mutationFn: async (n: any) => (await api.post('/cadastros/produtos', {
      codigo: n.codigo, descricao: n.descricao, unidade: n.unidade || 'UN',
      ncm: n.ncm || null, cfop: n.cfop || null, cst: n.cst || null,
      csosn: n.csosn || null, cest: n.cest || null,
      aliquota_icms: N(n.aliquota_icms), finalidade: n.finalidade || 'revenda',
    })).data,
    onSuccess: () => { toast.success('Produto criado'); setNovo(null); qc.invalidateQueries({ queryKey: ['tributacao-produtos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar produto'),
  })

  const { data: produtos = [], isLoading } = useQuery<Prod[]>({
    queryKey: ['tributacao-produtos', q, comST],
    queryFn: async () => (await api.get('/fiscal/tributacao-produtos', {
      params: { busca: q || undefined, com_st: comST || undefined },
    })).data,
  })

  const salvar = useMutation({
    mutationFn: async (p: Prod) => (await api.put('/fiscal/tributacao-produtos/' + p.id, {
      ncm: p.ncm, cfop: p.cfop, cest: p.cest, cst: p.cst, csosn: p.csosn,
      aliquota_icms: N(p.aliquota_icms), mva_st: N(p.mva_st), pauta_st: N(p.pauta_st),
      aliquota_icms_st: N(p.aliquota_icms_st),
      cst_pis: p.cst_pis, cst_cofins: p.cst_cofins,
      aliquota_pis: N(p.aliquota_pis), aliquota_cofins: N(p.aliquota_cofins),
      p_fcp: N(p.p_fcp), p_fcp_st: N(p.p_fcp_st),
      cst_ibs_cbs: p.cst_ibs_cbs, c_class_trib: p.c_class_trib,
      regra_imposto_id: p.regra_imposto_id ?? null,
    })).data,
    onSuccess: () => { toast.success('Tributação salva'); setEdit(null); qc.invalidateQueries({ queryKey: ['tributacao-produtos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
          <Receipt size={24} /> Tributação por Produto
        </h1>
        <button className="btn-primary" onClick={() => setNovo({ ...EMPTY_NOVO })}>
          <Plus size={14} /> Novo produto
        </button>
      </div>

      <div className="card p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-2.5 top-2.5 text-ink-400" />
          <input className="input pl-8" placeholder="Buscar por código, descrição ou NCM..."
            value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={e => {
              if (e.key !== 'Enter') return
              if (produtos.length > 0) { enterAbrePrimeiro(produtos, p => setEdit({ ...p }))(e); return }
              setQ(busca)
            }} />
        </div>
        <button className="btn-secondary" onClick={() => setQ(busca)}>Buscar</button>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={comST} onChange={e => setComST(e.target.checked)} />
          Só com ICMS-ST (CST 10)
        </label>

        <div className="w-full flex items-center gap-2 flex-wrap border-t border-ink-100 pt-2 mt-1">
          <span className="text-xs text-ink-500">
            {sel.size
              ? `${sel.size} selecionado(s)`
              : `Todos os ${produtos.length} do filtro atual`}
          </span>
          <select className="input w-auto min-w-[200px]" value={regraLote}
            onChange={e => setRegraLote(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— remover regra —</option>
            {regras.map((r: any) => (
              <option key={r.id} value={r.id}>{r.nome}</option>
            ))}
          </select>
          <button className="btn-secondary" disabled={aplicarLote.isPending}
            onClick={confirmarLote}>
            {aplicarLote.isPending ? 'Aplicando...' : 'Aplicar em lote'}
          </button>
          {sel.size > 0 && (
            <button className="text-xs text-ink-500 hover:underline"
              onClick={() => setSel(new Set())}>limpar seleção</button>
          )}
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th className="w-8"></th>
            <th>Código</th><th>Descrição</th><th>Regra</th><th>NCM</th><th>CFOP</th><th>CEST</th>
            <th>CST</th><th className="text-right">ICMS %</th><th className="text-right">Pauta ST</th>
            <th className="text-right">MVA %</th><th></th>
          </tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={10} className="text-center py-8 text-ink-400"><Loader2 className="animate-spin inline" size={16} /> Carregando...</td></tr>
            ) : produtos.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-ink-400">Nenhum produto</td></tr>
            ) : produtos.map(p => (
              <tr key={p.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => setEdit({ ...p })}>
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={sel.has(p.id)}
                    onChange={e => setSel(s => {
                      const n = new Set(s)
                      e.target.checked ? n.add(p.id) : n.delete(p.id)
                      return n
                    })} />
                </td>
                <td className="font-mono text-xs">{p.codigo}</td>
                <td className="max-w-[280px] truncate">{p.descricao}</td>
                <td className="text-xs">
                  {p.regra_imposto_id
                    ? <span className="px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 whitespace-nowrap">
                        {nomeRegra(p.regra_imposto_id)}
                      </span>
                    : <span className="text-ink-300">—</span>}
                </td>
                <td className="font-mono text-xs">{p.ncm}</td>
                <td className="font-mono text-xs">{p.cfop}</td>
                <td className="font-mono text-xs">{p.cest}</td>
                <td><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${p.cst === '10' ? 'bg-amber-100 text-amber-800' : 'bg-ink-100 text-ink-600'}`}>{p.cst || p.csosn || '—'}</span></td>
                <td className="text-right">{p.aliquota_icms != null ? Number(p.aliquota_icms).toFixed(2) : '—'}</td>
                <td className="text-right">{p.pauta_st != null ? Number(p.pauta_st).toFixed(2) : '—'}</td>
                <td className="text-right">{p.mva_st ? Number(p.mva_st).toFixed(2) : '—'}</td>
                <td className="text-right"><Pencil size={13} className="text-ink-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="px-5 py-4 border-b sticky top-0 bg-white">
              <h3 className="font-semibold text-lg">Tributação — {edit.codigo}</h3>
              <p className="text-sm text-ink-500 truncate">{edit.descricao}</p>
            </div>
            <div className="px-5 pt-4">
              <label className="label">Regra de imposto</label>
              <select className="input" value={edit.regra_imposto_id ?? ''}
                onChange={e => setEdit({ ...edit, regra_imposto_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">— usar os campos abaixo —</option>
                {regras.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
              {edit.regra_imposto_id && (
                <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                  <Lock size={11} />
                  A tributação vem da regra — os campos abaixo ficam guardados mas
                  não valem na emissão. Mudou a regra, muda este produto junto.
                </p>
              )}
            </div>

            <div className={`px-5 py-4 grid grid-cols-2 md:grid-cols-3 gap-3 ${
              edit.regra_imposto_id ? 'opacity-50' : ''}`}>
              {([
                ['ncm', 'NCM'], ['cfop', 'CFOP'], ['cest', 'CEST'],
                ['cst', 'CST (regime normal)'], ['csosn', 'CSOSN (Simples)'], ['aliquota_icms', 'ICMS %'],
                ['pauta_st', 'Pauta ST (base fixa/un)'], ['mva_st', 'MVA ST %'], ['aliquota_icms_st', 'Alíq. ICMS-ST %'],
                ['cst_pis', 'CST PIS'], ['cst_cofins', 'CST COFINS'],
                ['aliquota_pis', 'PIS %'], ['aliquota_cofins', 'COFINS %'],
                ['p_fcp', 'FCP %'], ['p_fcp_st', 'FCP ST %'],
                ['cst_ibs_cbs', 'CST IBS/CBS'], ['c_class_trib', 'cClassTrib'],
              ] as [keyof Prod, string][]).map(([k, label]) => (
                <div key={k}>
                  <label className="label">{label}</label>
                  {/* NCM e CEST continuam editáveis: são identidade do PRODUTO,
                      não tributação da operação — a regra não os define. */}
                  <input className="input" value={(edit as any)[k] ?? ''}
                    disabled={!!edit.regra_imposto_id && !['ncm', 'cest'].includes(k as string)}
                    onChange={e => setEdit({ ...edit, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <UfTributacao produtoId={edit.id} />
            <div className="px-5 py-4 border-t flex gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setEdit(null)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => salvar.mutate(edit)} disabled={salvar.isPending} className="btn-primary flex-1">
                {salvar.isPending ? 'Salvando (intraestadual)...' : 'Salvar intraestadual'}
              </button>
            </div>
          </div>
        </div>
      )}
      {novo && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl">
            <div className="px-5 py-4 border-b"><h3 className="font-semibold text-lg">Novo produto</h3>
              <p className="text-xs text-ink-500">Cria o produto com a tributação intraestadual. Por UF, edite depois.</p></div>
            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><label className="label">Código *</label><input className="input" value={novo.codigo} onChange={e => setNovo({ ...novo, codigo: e.target.value })} /></div>
              <div className="col-span-2"><label className="label">Descrição *</label><input className="input" value={novo.descricao} onChange={e => setNovo({ ...novo, descricao: e.target.value })} /></div>
              <div><label className="label">Unidade</label><input className="input" value={novo.unidade} onChange={e => setNovo({ ...novo, unidade: e.target.value })} /></div>
              <div><label className="label">NCM</label><input className="input" value={novo.ncm} onChange={e => setNovo({ ...novo, ncm: e.target.value })} /></div>
              <div><label className="label">CFOP</label><input className="input" value={novo.cfop} onChange={e => setNovo({ ...novo, cfop: e.target.value })} /></div>
              <div><label className="label">CST</label><input className="input" value={novo.cst} onChange={e => setNovo({ ...novo, cst: e.target.value })} /></div>
              <div><label className="label">CSOSN</label><input className="input" value={novo.csosn} onChange={e => setNovo({ ...novo, csosn: e.target.value })} /></div>
              <div><label className="label">CEST</label><input className="input" value={novo.cest} onChange={e => setNovo({ ...novo, cest: e.target.value })} /></div>
              <div><label className="label">ICMS %</label><input className="input" value={novo.aliquota_icms} onChange={e => setNovo({ ...novo, aliquota_icms: e.target.value })} /></div>
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setNovo(null)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => criar.mutate(novo)} disabled={!novo.codigo || !novo.descricao || criar.isPending} className="btn-primary flex-1">
                {criar.isPending ? 'Criando...' : 'Criar produto'}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-ink-400 flex items-center gap-1">
        <Download size={12} /> ICMS-ST sai com CST 10 (base de ST = pauta × quantidade).
      </p>
    </div>
  )
}
